import { useEffect, useState } from "react";
import { Copy, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { buildEA } from "@/lib/eaTemplates";
import { fmtUtc } from "@/lib/trading";
import { LiveTestPanel } from "@/components/LiveTestPanel";

const BASE = process.env.REACT_APP_BACKEND_URL;

const copy = (text) => navigator.clipboard.writeText(text).then(() => toast.success("Copied"));

const download = (platform, key) => {
  const blob = new Blob([buildEA(platform, BASE, key)], { type: "text/plain" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = platform === "mt4" ? "TradePulseSync.mq4" : "TradePulseSync.mq5";
  a.click();
  URL.revokeObjectURL(a.href);
};

const Row = ({ label, value, testId }) => (
  <div>
    <p className="text-[11px] font-mono uppercase text-slate-500 mb-1">{label}</p>
    <div className="flex items-center gap-2">
      <code className="flex-1 min-w-0 truncate text-xs bg-slate-900 border border-slate-800 rounded px-2 py-1.5" data-testid={testId}>{value}</code>
      <button onClick={() => copy(value)} className="p-1.5 text-slate-400 hover:text-slate-100" data-testid={`${testId}-copy`}><Copy className="h-4 w-4" /></button>
    </div>
  </div>
);

const STEPS = [
  "Download the EA for your platform below.",
  "In MetaTrader: File › Open Data Folder › MQL5/Experts (or MQL4/Experts) and paste the file. Restart MT or refresh the Navigator.",
  "Tools › Options › Expert Advisors: tick “Allow WebRequest for listed URL” and add the Allowed URL shown above.",
  "MT4 only: in Terminal › Account History, right-click › All History so older trades are visible to the EA.",
  "Drag the EA onto any chart and tick “Allow Algo Trading” (MT4: “Allow live trading”). Leave the terminal running (a VPS works best).",
  "Watch the Live test log below. “EA connected” means the setup worked. If nothing appears, paste your Experts tab below.",
  "Closed trades from the last 90 days import right away, then new ones every minute. The EA is read-only and never places trades.",
];

export const SyncDialog = ({ account, onClose, onChanged }) => {
  const [key, setKey] = useState(null);
  const current = key;
  useEffect(() => {
    if (account) api.get(`/accounts/${account.id}/sync-key`).then((r) => setKey(r.data.sync_key)).catch((e) => toast.error(errMsg(e)));
  }, [account]);
  const regen = async () => {
    if (!window.confirm("Generate a new key? The EA running with the old key will stop syncing.")) return;
    try { const { data } = await api.post(`/accounts/${account.id}/sync-key`); setKey(data.sync_key); onChanged(); toast.success("New sync key generated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!account} onOpenChange={(o) => { if (!o) { setKey(null); onClose(); } }}>
      <DialogContent className="bg-[#121722] border-slate-800 max-w-xl max-h-[90vh] overflow-y-auto" data-testid="sync-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Auto-sync · {account?.broker} #{account?.account_number}</DialogTitle>
          <DialogDescription>Connect your MT4/MT5 terminal. No API token or broker password needed.</DialogDescription>
        </DialogHeader>
        <p className="text-xs font-mono" data-testid="sync-status">
          {account?.last_sync_at ? <span className="text-emerald-400">● Last sync {fmtUtc(account.last_sync_at)} UTC</span> : <span className="text-slate-500">○ Not connected yet</span>}
        </p>
        <Row label="Allowed URL (add in MetaTrader)" value={BASE} testId="sync-allowed-url" />
        <Row label="Your private sync key" value={current || ""} testId="sync-key-value" />
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => download("mt5", current)} data-testid="download-ea-mt5" className="bg-emerald-600 hover:bg-emerald-500 text-white"><Download className="h-4 w-4 mr-1" />MT5 EA (.mq5)</Button>
          <Button onClick={() => download("mt4", current)} data-testid="download-ea-mt4" variant="outline" className="border-slate-700"><Download className="h-4 w-4 mr-1" />MT4 EA (.mq4)</Button>
          <Button onClick={regen} data-testid="regenerate-sync-key" variant="ghost" className="text-slate-400"><RefreshCw className="h-4 w-4 mr-1" />New key</Button>
        </div>
        <ol className="space-y-2 text-sm text-slate-300 list-decimal pl-5">{STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
        {account && <LiveTestPanel accountId={account.id} />}
      </DialogContent>
    </Dialog>
  );
};
